import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { ScrollRevealWrapper } from '../../../components/feature/ScrollRevealWrapper';

const pills = [
  { icon: 'ri-medal-line', text: 'NLP Practitioner Certificada' },
  { icon: 'ri-mic-line', text: 'Palestrante TEDx São Paulo' },
  { icon: 'ri-building-line', text: 'McKinsey · Google · Itaú BBA' },
  { icon: 'ri-heart-pulse-line', text: 'Baseado em PNL Clínica' },
];

const stats = [
  { end: 10000, suffix: '+', label: 'mulheres transformadas', icon: 'ri-group-line', prefix: '' },
  { end: 15, suffix: '+', label: 'anos de prática clínica', icon: 'ri-award-line', prefix: '' },
  { end: 4.9, suffix: '★', label: 'avaliação média', icon: 'ri-star-fill', prefix: '', decimals: 1 },
  { end: 12, suffix: '', label: 'países alcançados', icon: 'ri-global-line', prefix: '' },
];

function useCountUp(end: number, duration = 1800, decimals = 0, active = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(parseFloat((eased * end).toFixed(decimals)));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [active, end, duration, decimals]);
  return count;
}

function StatCard({ stat, active }: { stat: typeof stats[0]; active: boolean }) {
  const count = useCountUp(stat.end, 1800, stat.decimals ?? 0, active);
  const display =
    stat.end >= 1000
      ? `${(count / 1000).toFixed(count >= 1000 ? 0 : 1)}k`
      : stat.decimals
      ? count.toFixed(stat.decimals)
      : Math.floor(count).toString();

  return (
    <div className="group relative flex items-center gap-3 bg-gradient-to-br from-[#1a1508] to-[#111108] border border-gold-500/20 hover:border-gold-500/40 rounded-xl px-4 py-4 transition-all duration-300 overflow-hidden">
      {/* glow hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-gold-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-gold-600/30 to-gold-400/20 border border-gold-500/25 flex-shrink-0 relative z-10">
        <i className={`${stat.icon} text-gold-400 text-base`} />
      </div>
      <div className="relative z-10">
        <div className="font-display text-xl font-bold text-gold-300 leading-none">
          {stat.prefix}{display}{stat.suffix}
        </div>
        <div className="text-xs text-white/40 font-['Montserrat'] mt-0.5 leading-tight">{stat.label}</div>
      </div>
    </div>
  );
}

export default function BioRapida() {
  const statsRef = useRef<HTMLDivElement>(null);
  const [statsActive, setStatsActive] = useState(false);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setStatsActive(true); observer.disconnect(); } },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative bg-[#0a0a0a] py-20 px-6 overflow-hidden">
      {/* Linha topo */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-500/30 to-transparent" />

      {/* Fundo dourado suave */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_25%_50%,rgba(201,164,64,0.09),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_75%_80%,rgba(201,164,64,0.04),transparent_50%)]" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-[300px_1fr] gap-12 lg:gap-20 items-center">

          {/* Foto */}
          <ScrollRevealWrapper animation="fade-right">
            <div className="relative w-full max-w-xs mx-auto lg:mx-0">
              {/* Anel dourado de fundo */}
              <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-gold-500/15 via-gold-400/5 to-transparent blur-xl" />
              <div className="relative rounded-2xl overflow-hidden aspect-[3/4] border border-gold-500/25">
                <img
                  src="https://static.readdy.ai/image/9b313db6312c9605d9f9e3f145d3e29c/32f3bc36bea2a3e696d7bc6c9c1538c4.jpeg"
                  alt="Bia Holmes — Mental Performance Coach"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/80 via-transparent to-transparent" />
                {/* badge nome */}
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="bg-gradient-to-r from-[#0a0a0a]/95 to-[#1a1508]/90 backdrop-blur-sm border border-gold-500/30 rounded-xl px-4 py-3">
                    <p className="text-white font-bold text-sm font-['Montserrat'] tracking-wide">Bia Holmes</p>
                    <p className="text-gold-400 text-xs font-['Montserrat'] tracking-wider uppercase mt-0.5">
                      Mental Performance Coach
                    </p>
                  </div>
                </div>
              </div>
              {/* anéis decorativos */}
              <div className="absolute -bottom-3 -right-3 w-24 h-24 rounded-full border border-gold-500/15 pointer-events-none" />
              <div className="absolute -bottom-6 -right-6 w-36 h-36 rounded-full border border-gold-500/8 pointer-events-none" />
            </div>
          </ScrollRevealWrapper>

          {/* Conteúdo */}
          <ScrollRevealWrapper animation="fade-left">
            <div>
              <div className="inline-flex items-center gap-3 mb-5">
                <div className="h-px w-6 bg-gradient-to-r from-gold-500 to-gold-300" />
                <span className="text-gold-400 text-xs font-semibold tracking-[0.25em] uppercase font-['Montserrat']">
                  Quem é a Bia Holmes
                </span>
              </div>

              <h2 className="font-display text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
                A especialista por trás<br />
                do <span className="bg-gradient-to-r from-gold-400 to-amber-300 bg-clip-text text-transparent">Método.</span>
              </h2>

              <p className="text-white/60 font-['Montserrat'] text-base leading-relaxed mb-4 max-w-2xl">
                Bia Holmes é{' '}
                <strong className="text-white/90 font-semibold">Executive Mental Performance Coach</strong>{' '}
                com 15 anos de prática clínica em PNL. Ela ajuda mulheres executivas, empresárias e líderes a
                quebrarem padrões mentais invisíveis que travam o próximo salto — profissional e pessoal.
              </p>
              <p className="text-white/40 font-['Montserrat'] text-sm leading-relaxed mb-8 max-w-xl">
                Superou a síndrome do pânico com as mesmas técnicas que hoje aplica. Treinou líderes em McKinsey,
                Google Brasil, Itaú BBA e foi palestrante no TEDx São Paulo.
              </p>

              {/* Contadores animados */}
              <div ref={statsRef} className="grid grid-cols-2 gap-3 mb-8">
                {stats.map((s) => (
                  <StatCard key={s.label} stat={s} active={statsActive} />
                ))}
              </div>

              {/* Pills de credenciais */}
              <div className="flex flex-wrap gap-2 mb-8">
                {pills.map((p) => (
                  <div
                    key={p.text}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1a1508]/80 to-[#111]/80 border border-gold-500/18 rounded-full hover:border-gold-500/35 transition-colors"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${p.icon} text-gold-400 text-xs`} />
                    </div>
                    <span className="text-white/55 text-xs font-['Montserrat'] font-medium whitespace-nowrap">
                      {p.text}
                    </span>
                  </div>
                ))}
              </div>

              {/* Quote */}
              <blockquote className="relative bg-gradient-to-r from-gold-500/8 to-transparent border-l-2 border-gold-400/50 pl-5 pr-4 py-3 rounded-r-xl mb-8">
                <p className="text-white/50 text-sm italic font-['Montserrat'] leading-relaxed">
                  "O próximo nível que você busca não está no mercado. Está no padrão mental que roda em segundo
                  plano — e que pode ser reprogramado."
                </p>
                <cite className="text-gold-400 text-xs font-semibold font-['Montserrat'] not-italic mt-1.5 block">
                  — Bia Holmes
                </cite>
              </blockquote>

              <Link
                to="/sobre"
                className="inline-flex items-center gap-2.5 px-7 py-3.5 bg-gradient-to-r from-gold-500 to-gold-700 text-[#0a0a0a] rounded-full font-bold text-sm hover:from-gold-600 hover:to-gold-800 hover:scale-105 transition-all whitespace-nowrap font-['Montserrat'] cursor-pointer"
              >
                <i className="ri-user-line text-base" />
                Conhecer a Trajetória Completa
                <i className="ri-arrow-right-line text-base" />
              </Link>
            </div>
          </ScrollRevealWrapper>
        </div>
      </div>

      {/* Linha base */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-500/20 to-transparent" />
    </section>
  );
}
